import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { okrsApi } from '@/utils/api';
import type {
  Objective,
  KeyResult,
  KeyResultProgress,
  OKRDashboardData,
  OKRTree,
  OKRCycle,
  OKRLevel,
  OKRStatusNew,
} from '@/types';

// Filter parameters for objectives
interface ObjectiveFilters {
  level?: OKRLevel;
  quarter?: number;
  fiscalYear?: number;
  status?: OKRStatusNew;
  epicId?: string;
  investibleStructureId?: string;
  programId?: string;
  parentId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// State interface
interface OKRsState {
  // Objectives
  objectives: Objective[];
  currentObjective: Objective | null;
  objectiveTree: OKRTree | null;

  // Dashboard
  dashboard: OKRDashboardData | null;
  cycles: OKRCycle[];
  currentCycle: { fiscalYear: number; quarter: number } | null;

  // Filters
  filters: ObjectiveFilters;

  // View mode
  viewMode: 'list' | 'tree';

  // Key Results
  keyResults: KeyResult[];
  currentKeyResult: KeyResult | null;
  progressHistory: KeyResultProgress[];

  // Loading states
  isLoading: boolean;
  isLoadingTree: boolean;
  isLoadingDashboard: boolean;
  isLoadingProgress: boolean;

  // Errors
  error: string | null;

  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: OKRsState = {
  objectives: [],
  currentObjective: null,
  objectiveTree: null,
  dashboard: null,
  cycles: [],
  currentCycle: null,
  filters: {},
  viewMode: 'list',
  keyResults: [],
  currentKeyResult: null,
  progressHistory: [],
  isLoading: false,
  isLoadingTree: false,
  isLoadingDashboard: false,
  isLoadingProgress: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

// ============================================================================
// OBJECTIVE THUNKS
// ============================================================================

export const fetchObjectives = createAsyncThunk(
  'okrs/fetchObjectives',
  async (params: ObjectiveFilters = {}) => {
    const response = await okrsApi.objectives.getAll(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch objectives');
    }
    return response.data;
  }
);

export const fetchObjectiveById = createAsyncThunk(
  'okrs/fetchObjectiveById',
  async (id: string) => {
    const response = await okrsApi.objectives.getById(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch objective');
    }
    return response.data;
  }
);

export const fetchObjectiveTree = createAsyncThunk(
  'okrs/fetchObjectiveTree',
  async (params: { quarter?: number; fiscalYear?: number } = {}) => {
    const response = await okrsApi.objectives.getTree(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch objective tree');
    }
    return response.data;
  }
);

export const createObjective = createAsyncThunk(
  'okrs/createObjective',
  async (data: {
    title: string;
    description?: string;
    epicId?: string;
    investibleStructureId?: string;
    programId?: string;
    quarter: number;
    fiscalYear: number;
    status?: string;
    parentObjectiveId?: string;
  }) => {
    const response = await okrsApi.objectives.create(data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create objective');
    }
    return response.data;
  }
);

export const updateObjective = createAsyncThunk(
  'okrs/updateObjective',
  async ({ id, data }: {
    id: string;
    data: {
      title?: string;
      description?: string | null;
      quarter?: number;
      fiscalYear?: number;
      status?: string;
      parentObjectiveId?: string | null;
    };
  }) => {
    const response = await okrsApi.objectives.update(id, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update objective');
    }
    return response.data;
  }
);

export const updateObjectiveStatus = createAsyncThunk(
  'okrs/updateObjectiveStatus',
  async ({ id, status }: { id: string; status: string }) => {
    const response = await okrsApi.objectives.updateStatus(id, status);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update objective status');
    }
    return response.data;
  }
);

export const deleteObjective = createAsyncThunk(
  'okrs/deleteObjective',
  async (id: string) => {
    const response = await okrsApi.objectives.delete(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete objective');
    }
    return id;
  }
);

// ============================================================================
// KEY RESULT THUNKS
// ============================================================================

export const fetchKeyResults = createAsyncThunk(
  'okrs/fetchKeyResults',
  async (params: { objectiveId?: string; search?: string; page?: number; limit?: number } = {}) => {
    const response = await okrsApi.keyResults.getAll(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch key results');
    }
    return response.data;
  }
);

export const createKeyResult = createAsyncThunk(
  'okrs/createKeyResult',
  async (data: {
    description: string;
    targetValue: number;
    currentValue?: number;
    startValue?: number;
    unit?: string;
    confidence?: number;
    objectiveId: string;
  }) => {
    const response = await okrsApi.keyResults.create(data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create key result');
    }
    return response.data;
  }
);

export const updateKeyResult = createAsyncThunk(
  'okrs/updateKeyResult',
  async ({ id, data }: {
    id: string;
    data: {
      description?: string;
      targetValue?: number;
      currentValue?: number | null;
      startValue?: number;
      unit?: string | null;
      confidence?: number | null;
    };
  }) => {
    const response = await okrsApi.keyResults.update(id, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update key result');
    }
    return response.data;
  }
);

export const deleteKeyResult = createAsyncThunk(
  'okrs/deleteKeyResult',
  async (id: string) => {
    const response = await okrsApi.keyResults.delete(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete key result');
    }
    return id;
  }
);

export const recordKeyResultProgress = createAsyncThunk(
  'okrs/recordKeyResultProgress',
  async ({ id, value, note }: { id: string; value: number; note?: string }) => {
    const response = await okrsApi.keyResults.recordProgress(id, { value, note });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to record progress');
    }
    return response.data;
  }
);

export const fetchProgressHistory = createAsyncThunk(
  'okrs/fetchProgressHistory',
  async ({ id, page, limit }: { id: string; page?: number; limit?: number }) => {
    const response = await okrsApi.keyResults.getProgressHistory(id, { page, limit });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch progress history');
    }
    return response.data;
  }
);

// ============================================================================
// DASHBOARD THUNKS
// ============================================================================

export const fetchOKRDashboard = createAsyncThunk(
  'okrs/fetchOKRDashboard',
  async (params: {
    fiscalYear?: number;
    quarter?: number;
    level?: 'IS' | 'PROGRAM' | 'EPIC' | 'ALL';
    investibleStructureId?: string;
    programId?: string;
  } = {}) => {
    const response = await okrsApi.dashboard.get(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch OKR dashboard');
    }
    return response.data;
  }
);

export const fetchOKRCycles = createAsyncThunk(
  'okrs/fetchOKRCycles',
  async () => {
    const response = await okrsApi.dashboard.getCycles();
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch OKR cycles');
    }
    return response.data;
  }
);

// ============================================================================
// LEGACY THUNKS (for backward compatibility)
// ============================================================================

export const fetchOKRs = createAsyncThunk(
  'okrs/fetchOKRs',
  async (params: { epicId?: string; page?: number; limit?: number } = {}) => {
    const response = await okrsApi.getAll(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch OKRs');
    }
    return response.data;
  }
);

export const fetchOKRById = createAsyncThunk(
  'okrs/fetchOKRById',
  async (id: string) => {
    const response = await okrsApi.getById(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch OKR');
    }
    return response.data;
  }
);

export const createOKR = createAsyncThunk(
  'okrs/createOKR',
  async (data: any) => {
    const response = await okrsApi.create(data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create OKR');
    }
    return response.data;
  }
);

export const updateOKR = createAsyncThunk(
  'okrs/updateOKR',
  async ({ id, data }: { id: string; data: any }) => {
    const response = await okrsApi.update(id, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update OKR');
    }
    return response.data;
  }
);

export const updateOKRProgress = createAsyncThunk(
  'okrs/updateOKRProgress',
  async ({ id, currentValue }: { id: string; currentValue: number }) => {
    const response = await okrsApi.updateProgress(id, currentValue);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update OKR progress');
    }
    return response.data;
  }
);

export const deleteOKR = createAsyncThunk(
  'okrs/deleteOKR',
  async (id: string) => {
    const response = await okrsApi.delete(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete OKR');
    }
    return id;
  }
);

// ============================================================================
// SLICE
// ============================================================================

const okrsSlice = createSlice({
  name: 'okrs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentObjective: (state, action: PayloadAction<Objective | null>) => {
      state.currentObjective = action.payload;
    },
    setCurrentKeyResult: (state, action: PayloadAction<KeyResult | null>) => {
      state.currentKeyResult = action.payload;
    },
    setFilters: (state, action: PayloadAction<ObjectiveFilters>) => {
      state.filters = action.payload;
    },
    updateFilter: (state, action: PayloadAction<Partial<ObjectiveFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setViewMode: (state, action: PayloadAction<'list' | 'tree'>) => {
      state.viewMode = action.payload;
    },
    setCurrentCycle: (state, action: PayloadAction<{ fiscalYear: number; quarter: number } | null>) => {
      state.currentCycle = action.payload;
    },
    updateObjectiveInList: (state, action: PayloadAction<Objective>) => {
      const index = state.objectives.findIndex(obj => obj.id === action.payload.id);
      if (index !== -1) {
        state.objectives[index] = action.payload;
      }
    },
    clearProgressHistory: (state) => {
      state.progressHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ============================================
      // Fetch Objectives
      // ============================================
      .addCase(fetchObjectives.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.objectives = payload.objectives || [];
        if (payload.pagination) {
          state.pagination = payload.pagination;
        }
      })
      .addCase(fetchObjectives.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch objectives';
      })

      // ============================================
      // Fetch Objective by ID
      // ============================================
      .addCase(fetchObjectiveById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchObjectiveById.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.currentObjective = payload.objective || payload;
      })
      .addCase(fetchObjectiveById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch objective';
      })

      // ============================================
      // Fetch Objective Tree
      // ============================================
      .addCase(fetchObjectiveTree.pending, (state) => {
        state.isLoadingTree = true;
        state.error = null;
      })
      .addCase(fetchObjectiveTree.fulfilled, (state, action) => {
        state.isLoadingTree = false;
        const payload = action.payload as any;
        state.objectiveTree = payload.tree || null;
      })
      .addCase(fetchObjectiveTree.rejected, (state, action) => {
        state.isLoadingTree = false;
        state.error = action.error.message || 'Failed to fetch objective tree';
      })

      // ============================================
      // Create Objective
      // ============================================
      .addCase(createObjective.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createObjective.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        const newObjective = payload.objective || payload;
        state.objectives.unshift(newObjective);
      })
      .addCase(createObjective.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create objective';
      })

      // ============================================
      // Update Objective
      // ============================================
      .addCase(updateObjective.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateObjective.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        const updatedObjective = payload.objective || payload;
        const index = state.objectives.findIndex(obj => obj.id === updatedObjective.id);
        if (index !== -1) {
          state.objectives[index] = updatedObjective;
        }
        if (state.currentObjective?.id === updatedObjective.id) {
          state.currentObjective = updatedObjective;
        }
      })
      .addCase(updateObjective.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update objective';
      })

      // ============================================
      // Update Objective Status
      // ============================================
      .addCase(updateObjectiveStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateObjectiveStatus.fulfilled, (state, action) => {
        const payload = action.payload as any;
        const updatedObjective = payload.objective || payload;
        const index = state.objectives.findIndex(obj => obj.id === updatedObjective.id);
        if (index !== -1) {
          state.objectives[index] = updatedObjective;
        }
        if (state.currentObjective?.id === updatedObjective.id) {
          state.currentObjective = updatedObjective;
        }
      })
      .addCase(updateObjectiveStatus.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update objective status';
      })

      // ============================================
      // Delete Objective
      // ============================================
      .addCase(deleteObjective.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteObjective.fulfilled, (state, action) => {
        const deletedId = action.payload as string;
        state.objectives = state.objectives.filter(obj => obj.id !== deletedId);
        if (state.currentObjective?.id === deletedId) {
          state.currentObjective = null;
        }
      })
      .addCase(deleteObjective.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete objective';
      })

      // ============================================
      // Key Result CRUD
      // ============================================
      .addCase(fetchKeyResults.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKeyResults.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.keyResults = payload.keyResults || [];
      })
      .addCase(fetchKeyResults.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch key results';
      })

      .addCase(createKeyResult.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createKeyResult.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        const newKeyResult = payload.keyResult || payload;
        state.keyResults.unshift(newKeyResult);
        // Update current objective's key results if it matches
        if (state.currentObjective && state.currentObjective.id === newKeyResult.objectiveId) {
          state.currentObjective.keyResults = [
            ...(state.currentObjective.keyResults || []),
            newKeyResult,
          ];
        }
      })
      .addCase(createKeyResult.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create key result';
      })

      .addCase(updateKeyResult.fulfilled, (state, action) => {
        const payload = action.payload as any;
        const updatedKeyResult = payload.keyResult || payload;
        const index = state.keyResults.findIndex(kr => kr.id === updatedKeyResult.id);
        if (index !== -1) {
          state.keyResults[index] = updatedKeyResult;
        }
        // Update current objective's key results if it matches
        if (state.currentObjective?.keyResults) {
          const krIndex = state.currentObjective.keyResults.findIndex(kr => kr.id === updatedKeyResult.id);
          if (krIndex !== -1) {
            state.currentObjective.keyResults[krIndex] = updatedKeyResult;
          }
        }
      })

      .addCase(deleteKeyResult.fulfilled, (state, action) => {
        const deletedId = action.payload as string;
        state.keyResults = state.keyResults.filter(kr => kr.id !== deletedId);
        // Update current objective's key results if it matches
        if (state.currentObjective?.keyResults) {
          state.currentObjective.keyResults = state.currentObjective.keyResults.filter(kr => kr.id !== deletedId);
        }
      })

      // ============================================
      // Record Progress
      // ============================================
      .addCase(recordKeyResultProgress.pending, (state) => {
        state.isLoadingProgress = true;
        state.error = null;
      })
      .addCase(recordKeyResultProgress.fulfilled, (state, action) => {
        state.isLoadingProgress = false;
        const payload = action.payload as any;
        const updatedKeyResult = payload.keyResult;
        const progressRecord = payload.progressRecord;

        // Update key result in state
        if (updatedKeyResult) {
          const index = state.keyResults.findIndex(kr => kr.id === updatedKeyResult.id);
          if (index !== -1) {
            state.keyResults[index] = updatedKeyResult;
          }
          // Update current objective's key results
          if (state.currentObjective?.keyResults) {
            const krIndex = state.currentObjective.keyResults.findIndex(kr => kr.id === updatedKeyResult.id);
            if (krIndex !== -1) {
              state.currentObjective.keyResults[krIndex] = updatedKeyResult;
            }
          }
        }

        // Add to progress history
        if (progressRecord) {
          state.progressHistory.unshift(progressRecord);
        }
      })
      .addCase(recordKeyResultProgress.rejected, (state, action) => {
        state.isLoadingProgress = false;
        state.error = action.error.message || 'Failed to record progress';
      })

      // ============================================
      // Fetch Progress History
      // ============================================
      .addCase(fetchProgressHistory.pending, (state) => {
        state.isLoadingProgress = true;
        state.error = null;
      })
      .addCase(fetchProgressHistory.fulfilled, (state, action) => {
        state.isLoadingProgress = false;
        const payload = action.payload as any;
        state.progressHistory = payload.history || [];
        if (payload.keyResult) {
          state.currentKeyResult = payload.keyResult;
        }
      })
      .addCase(fetchProgressHistory.rejected, (state, action) => {
        state.isLoadingProgress = false;
        state.error = action.error.message || 'Failed to fetch progress history';
      })

      // ============================================
      // Dashboard
      // ============================================
      .addCase(fetchOKRDashboard.pending, (state) => {
        state.isLoadingDashboard = true;
        state.error = null;
      })
      .addCase(fetchOKRDashboard.fulfilled, (state, action) => {
        state.isLoadingDashboard = false;
        state.dashboard = action.payload as OKRDashboardData;
      })
      .addCase(fetchOKRDashboard.rejected, (state, action) => {
        state.isLoadingDashboard = false;
        state.error = action.error.message || 'Failed to fetch OKR dashboard';
      })

      // ============================================
      // Cycles
      // ============================================
      .addCase(fetchOKRCycles.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchOKRCycles.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.cycles = payload.cycles || [];
        state.currentCycle = payload.currentCycle || null;
      })
      .addCase(fetchOKRCycles.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch OKR cycles';
      })

      // ============================================
      // Legacy OKR Thunks (for backward compatibility)
      // ============================================
      .addCase(fetchOKRs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOKRs.fulfilled, (state, action) => {
        state.isLoading = false;
        // Map legacy OKRs to objectives format if needed
        const payload = action.payload as any;
        // Store as-is for backward compatibility
        if (payload.objectives) {
          state.objectives = payload.objectives;
        }
        if (payload.pagination) {
          state.pagination = payload.pagination;
        }
      })
      .addCase(fetchOKRs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch OKRs';
      });
  },
});

export const {
  clearError,
  setCurrentObjective,
  setCurrentKeyResult,
  setFilters,
  updateFilter,
  clearFilters,
  setViewMode,
  setCurrentCycle,
  updateObjectiveInList,
  clearProgressHistory,
} = okrsSlice.actions;

export default okrsSlice.reducer;
